import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ASSETS } from "../../config";
import { CtaButton, FlagIT } from "./primitives";
import { VslPlayer } from "./VslPlayer";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden pb-[0.08em] ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pb-24 pt-16 sm:pt-20"
    >
      <motion.div style={{ y: yBg }} className="absolute inset-0" aria-hidden>
        <img
          src={ASSETS.heroBg}
          alt=""
          className="h-[115%] w-full object-cover opacity-25 grayscale"
        />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-ink to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto max-w-7xl px-6 sm:px-8"
      >
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.35em] text-white/70"
          data-testid="hero-overline"
        >
          Mappa del Tempio — Coaching cristiano di ascolto{" "}
          <FlagIT className="ml-1 h-3 w-[18px] align-middle" />
        </motion.p>

        <h1 className="mt-8 font-serif font-bold uppercase leading-[1.02] tracking-tight text-white text-[clamp(2.1rem,7.5vw,5.6rem)]">
          <MaskedLine delay={0.35}>Scopri da dove inizia</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="text-gold">la tua trasformazione</span>
          </MaskedLine>
          <MaskedLine delay={0.65}>con la Mappa del Tempio.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-white"
        >
          Un incontro <span className="text-gold">privato e gratuito di 30 minuti</span> per fare
          chiarezza sulla tua situazione, riconoscere la battaglia che stai vivendo e capire da
          dove ripartire, con Dio al centro.
        </motion.p>

        <div className="mt-14 grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <VslPlayer />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="lg:col-span-5"
          >
            <p className="text-base sm:text-lg font-light leading-relaxed text-white/80">
              Guarda questo breve video: ti spieghiamo cos'è la Mappa del Tempio e come può
              aiutarti a fare chiarezza nella tua vita.
            </p>
            <div className="mt-8">
              <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-gold">
                Hai commentato "Tempio" sui social? Sei nel posto giusto.
              </p>
              <CtaButton testId="hero-cta" large className="w-full sm:w-auto">
                Richiedi la tua Mappa del Tempio
              </CtaButton>
              <p className="mt-4 text-sm font-light text-white/60">
                Gratuita • Privata • 30 minuti • Nessun obbligo
              </p>
              <p className="mt-5 border-l-2 border-gold/50 pl-4 text-xs sm:text-sm font-light italic leading-relaxed text-white/55">
                Le nostre radici sono cristiane. Puoi avvicinarti anche se sei in ricerca o hai
                dubbi sulla fede.
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
